import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Box, Typography, TextField, Button, CircularProgress, Alert,
} from '@mui/material';
import { LockReset, Close } from '@mui/icons-material';
import { authApi } from '../services/hrmsApi';

const EMPTY_FORM = { currentPassword: '', newPassword: '', confirmNewPassword: '' };

export default function ChangePasswordDialog({ open, onClose }) {
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [result,  setResult]  = useState(null);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleClose = () => {
    if (saving) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setResult(null);
    onClose();
  };

  const validate = () => {
    const errs = {};
    if (!form.currentPassword) errs.currentPassword = 'Required';
    if (!form.newPassword) errs.newPassword = 'Required';
    else if (form.newPassword.length < 6) errs.newPassword = 'Min 6 characters';
    if (form.confirmNewPassword !== form.newPassword) errs.confirmNewPassword = 'Passwords do not match';
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    setResult(null);
    try {
      await authApi.changePassword(form.currentPassword, form.newPassword);
      setResult({ success: true, message: 'Password updated successfully.' });
      setForm(EMPTY_FORM);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password.';
      setResult({ success: false, message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockReset color="primary" />
          <Typography variant="h6" fontWeight={700}>Change Password</Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={saving} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {result && <Alert severity={result.success ? 'success' : 'error'}>{result.message}</Alert>}
          <TextField
            label="Current Password *" type="password" size="small" fullWidth
            value={form.currentPassword} onChange={setField('currentPassword')}
            error={!!errors.currentPassword} helperText={errors.currentPassword}
          />
          <TextField
            label="New Password *" type="password" size="small" fullWidth
            value={form.newPassword} onChange={setField('newPassword')}
            error={!!errors.newPassword} helperText={errors.newPassword}
          />
          <TextField
            label="Confirm New Password *" type="password" size="small" fullWidth
            value={form.confirmNewPassword} onChange={setField('confirmNewPassword')}
            error={!!errors.confirmNewPassword} helperText={errors.confirmNewPassword}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LockReset />}>
          {saving ? 'Updating…' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
